/* global React, Icon, Card, Button, Segmented, PlummeI18n */
const { useState: generatedInsightUseState } = React;

const insightRows = (items) => Array.isArray(items) ? items.filter((item) => item?.title || item?.detail) : [];

const AII_INSIGHT_TARGETS = { liquidity: { tab: 'likviditet' }, sales: { tab: 'salg' }, costs: { tab: 'kostnader' }, budget: { tab: 'budsjett' }, tax: { tab: 'skatt' }, invoices: { route: 'fakturaliste' }, vouchers: { route: 'allebilag' }, customers: { route: 'kunder' }, suppliers: { route: 'leverandorer' } };
function AiGeneratedEvidence({ item, rank, onOpen }) {
  if (!item) return null;
  return <div className="aii-gen-evidence">
    {rank != null && <span className="aii-gen-rank">{rank}</span>}
    <div className="aii-gen-evidence-body">
      <div className="aii-gen-evidence-title">{item.title}</div>
      {item.detail && <div className="aii-gen-evidence-detail">{item.detail}</div>}
      <div className="aii-gen-tags">
        <span className={'aii-gen-tag type-' + item.evidenceType}>{t('adv.generated.type.' + item.evidenceType)}</span>
        <span className={'aii-gen-tag confidence-' + item.confidence}>{t('adv.generated.confidence.' + item.confidence)}</span>
        {item.impact && <strong>{item.impact}</strong>}
        {onOpen && AII_INSIGHT_TARGETS[item.target] && <Button size="sm" variant="ghost" onClick={() => onOpen({ ...AII_INSIGHT_TARGETS[item.target], title: item.title, text: item.detail })} data-help={t('helpTopics.aiTask.body')} data-help-title={t('helpTopics.aiTask.title')}>{t('adv.handoff.start')}</Button>}
      </div>
    </div>
  </div>;
}

function AiGeneratedInsightPanel({ insights, loading, error, onRefresh, onAlreadyCurrent, onOpen, fallbackTasks = [] }) {
  const [kind, setKind] = generatedInsightUseState(() => ['daily', 'monthly', 'previousMonthly'].find((key) => window.PlummeAdvisorInsightPresentation.usable(insights?.[key])) || 'daily');
  const selectedByUser = React.useRef(false);
  React.useEffect(() => {
    if (selectedByUser.current || window.PlummeAdvisorInsightPresentation.usable(insights?.[kind])) return;
    const available = ['daily', 'monthly', 'previousMonthly'].find((key) => window.PlummeAdvisorInsightPresentation.usable(insights?.[key]));
    if (available) setKind(available);
  }, [insights, kind]);
  const insight = window.PlummeAdvisorInsightPresentation.select(insights?.[kind]);
  const incompatible = insights?.[kind] && !insight;
  const refreshBlocked = insight?.refreshBlocked === true;
  const refreshReason = insight?.refreshReason || '';
  const changes = insightRows(insight?.changes);
  const actions = insightRows(insight?.actions);
  const opportunities = insightRows(insight?.opportunities);
  const taxChecks = insightRows(insight?.taxChecks);
  const accountantInvolved = window.PlummeBackend?.getAiAdvisorProfile?.()?.accountantInvolved === true;
  // Weekly or monthly cadence: the daily insight is only regenerated automatically
  // from this date (see advisorPreferences.ts on the backend).
  const cadenceNext = kind === 'daily' && insight?.cadence && insight.cadence !== 'daily' && insight.nextScheduledDate
    ? insight.nextScheduledDate : null;
  const errorText = error?.code === 'resource-exhausted' || error?.status === 429
    ? t('adv.generated.limitReached')
    : error ? t('adv.generated.failed') : incompatible ? t('adv.generated.needsUpdate') : t('adv.generated.empty');

  return <Card className="aii-generated">
    <div className="aii-generated-head">
      <div>
        <div className="aii-generated-kicker"><Icon name="sparkle" size={15} />{t('adv.generated.kicker')}</div>
        <div className="aii-generated-title">{insight?.headline || t(loading ? 'adv.generated.loading' : incompatible ? 'adv.generated.outdatedTitle' : 'adv.generated.unavailable')}</div>
        {insight?.generatedAt && <div className="aii-generated-time">
          {t('adv.generated.updated', { date: PlummeI18n.dateTime(insight.generatedAt, { dateStyle: 'medium', timeStyle: 'short' }) })}
          {cadenceNext && <React.Fragment> · {t('adv.generated.cadenceNext', { date: PlummeI18n.date(cadenceNext, { day: 'numeric', month: 'short' }) })}</React.Fragment>}
        </div>}
      </div>
      <Segmented value={kind} onChange={(value) => { selectedByUser.current = true; setKind(value); }} options={[
        { value: 'daily', label: t('adv.generated.daily') },
        { value: 'monthly', label: t('adv.generated.monthly') },
        { value: 'previousMonthly', label: t('adv.generated.previousMonthly') },
      ]} />
    </div>

    {loading && !insight && <div className="aii-generated-loading">{t('adv.generated.loading')}</div>}
    {!loading && !insight && <div className="aii-generated-empty">
      <p>{errorText}</p>
      <Button variant="ghost" size="sm" icon="refresh" onClick={() => onRefresh?.(kind)} data-help={t('helpTopics.generatedReport.body')} data-help-title={t('helpTopics.generatedReport.title')}>{t('adv.generated.refresh')}</Button>
    </div>}
    {!loading && !insight && kind !== 'previousMonthly' && fallbackTasks.length > 0 && <section className="aii-analysis-fallback">
      <h4>{t('adv.generated.registeredTitle')}</h4><p>{t('adv.generated.registeredBasis')}</p>
      {fallbackTasks.map(({ rec }) => <div key={rec.id} className="aii-gen-evidence"><div className="aii-gen-evidence-body">
        <strong>{rec.title || rec.text}</strong>{rec.impact && <p>{rec.impactLbl}: {rec.impact}</p>}
        {rec.route && <Button size="sm" variant="ghost" onClick={() => onOpen?.(rec)} data-help={t('helpTopics.aiTask.body')} data-help-title={t('helpTopics.aiTask.title')}>{t('adv.handoff.start')}</Button>}
      </div></div>)}
    </section>}
    {insight && <React.Fragment>
      <p className="aii-generated-summary">{insight.summary}</p>

      {insight.topOpportunity?.title && <section className="aii-generated-opportunity">
        <div className="aii-generated-section-title">{t('adv.generated.topOpportunity')}</div>
        <AiGeneratedEvidence onOpen={onOpen} item={insight.topOpportunity} />
      </section>}

      <div className="aii-generated-columns">
        {changes.length > 0 && <section>
          <div className="aii-generated-section-title">{t('adv.generated.changes')}</div>
          {changes.map((item, index) => <AiGeneratedEvidence onOpen={onOpen} key={index} item={item} />)}
        </section>}
        {actions.length > 0 && <section>
          <div className="aii-generated-section-title">{t('adv.generated.actions')}</div>
          {actions.map((item, index) => <AiGeneratedEvidence onOpen={onOpen} key={index} item={item} rank={index + 1} />)}
        </section>}
      </div>

      {(opportunities.length > 0 || taxChecks.length > 0) && <details className="aii-generated-more">
        <summary>{t('adv.generated.more')}</summary>
        <div className="aii-generated-columns">
          {opportunities.length > 0 && <section>
            <div className="aii-generated-section-title">{t('adv.generated.opportunities')}</div>
            {opportunities.map((item, index) => <AiGeneratedEvidence onOpen={onOpen} key={index} item={item} />)}
          </section>}
          {taxChecks.length > 0 && <section>
            <div className="aii-generated-section-title">{t('adv.generated.taxChecks')}</div>
            {accountantInvolved && <p className="t-xs muted">{t('adv.profile.accountantNote')}</p>}
            {taxChecks.map((item, index) => <AiGeneratedEvidence onOpen={onOpen} key={index} item={item} />)}
          </section>}
        </div>
      </details>}

      {insight.nextStep && <div className="aii-generated-next"><Icon name="arrowRight" size={16} /><div><b>{t('adv.generated.nextStep')}</b><span>{insight.nextStep}</span></div></div>}
      {insight.dataCoverage?.transactionDetail === 'aggregate' && <div className="aii-generated-coverage">
        <Icon name="info" size={15} />{t('adv.generated.importCoverage')}
      </div>}
      {error && <div className="aii-generated-stale">{errorText} {t('adv.generated.previousKept')}</div>}
      <div className="aii-generated-foot">
        <span>{t('adv.generated.readOnly')}</span>
        {(insight.refreshAvailable || refreshBlocked || kind === 'daily') && <Button variant="ghost" size="sm" icon={refreshBlocked ? "check" : "refresh"} disabled={loading}
          onClick={() => refreshBlocked ? onAlreadyCurrent?.(kind, refreshReason) : onRefresh?.(kind)} data-help={t('helpTopics.generatedReport.body')} data-help-title={t('helpTopics.generatedReport.title')}>
          {refreshBlocked
            ? t(refreshReason === 'unchanged'
              ? 'adv.generated.noDataChanges'
              : refreshReason === 'cooldown'
                ? 'adv.generated.monthCooldown'
              : kind === 'daily'
                ? 'adv.generated.todayCurrent'
                : kind === 'previousMonthly'
                  ? 'adv.generated.previousMonthCurrent'
                  : 'adv.generated.monthCurrent')
            : t('adv.generated.refresh')}
        </Button>}
      </div>
    </React.Fragment>}
  </Card>;
}

Object.assign(window, { AiGeneratedInsightPanel });
window.dispatchEvent?.(new CustomEvent('plumme-page-registered', { detail: { pages: ['AiGeneratedInsightPanel'] } }));
